import React from 'react';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-purple-400 hover:text-purple-300 mb-4 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </button>
          <h1 className="text-4xl font-bold text-white mb-2">Privacy Policy</h1>
          <p className="text-gray-400">Last updated: October 15, 2025</p>
        </div>

        {/* Content */}
        <div className="reaction-card space-y-6 text-gray-300">
          <section>
            <h2 className="text-2xl font-bold text-white mb-3">1. Information We Collect</h2>
            <p className="mb-2">We collect the following types of information:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong className="text-white">Account Information:</strong> Email address, full name</li>
              <li><strong className="text-white">Wallet Information:</strong> Solana wallet addresses for payments</li>
              <li><strong className="text-white">Transaction Data:</strong> Order history, payment records, balance transactions</li>
              <li><strong className="text-white">Usage Data:</strong> IP address, browser type, device information</li>
              <li><strong className="text-white">Communication Data:</strong> Support tickets, email correspondence</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-3">2. How We Use Your Information</h2>
            <p className="mb-2">Your information is used for:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Processing orders and payments</li>
              <li>Managing your account and providing customer support</li>
              <li>Sending order confirmations and status updates</li>
              <li>Improving our services and user experience</li>
              <li>Preventing fraud and ensuring security</li>
              <li>Complying with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-3">3. Data Storage and Security</h2>
            <p className="mb-2">We take data security seriously:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Data is stored in secure MongoDB databases</li>
              <li>Passwords are hashed using bcrypt encryption</li>
              <li>All connections use HTTPS encryption</li>
              <li>Regular security audits and updates</li>
              <li>Access controls and authentication measures</li>
              <li>Data backup and disaster recovery procedures</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-3">4. Data Sharing and Disclosure</h2>
            <p className="mb-2">We do not sell your personal information. We may share data with:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong className="text-white">Service Providers:</strong> Payment processors, hosting services</li>
              <li><strong className="text-white">Legal Requirements:</strong> When required by law or to protect rights</li>
              <li><strong className="text-white">Business Transfers:</strong> In case of merger, acquisition, or sale</li>
            </ul>
            <p className="mt-2">
              We ensure all third parties comply with privacy standards and use your data only for specified purposes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-3">5. Cookies and Tracking</h2>
            <p className="mb-2">We use cookies and similar technologies for:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Maintaining user sessions</li>
              <li>Remembering preferences and settings</li>
              <li>Analyzing site usage and performance</li>
              <li>Improving user experience</li>
            </ul>
            <p className="mt-2">
              You can control cookie settings through your browser, but this may affect site functionality.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-3">6. Your Privacy Rights</h2>
            <p className="mb-2">You have the right to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong className="text-white">Access:</strong> Request a copy of your personal data</li>
              <li><strong className="text-white">Correction:</strong> Update or correct inaccurate information</li>
              <li><strong className="text-white">Deletion:</strong> Request deletion of your account and data</li>
              <li><strong className="text-white">Portability:</strong> Receive your data in a portable format</li>
              <li><strong className="text-white">Opt-out:</strong> Unsubscribe from marketing communications</li>
            </ul>
            <p className="mt-2">
              To exercise these rights, contact us through our support system.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-3">7. Data Retention</h2>
            <p>
              We retain your personal information for as long as necessary to provide services and comply with legal obligations. 
              Transaction records are kept for accounting and legal purposes. Inactive accounts may be deleted after extended periods.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-3">8. Children's Privacy</h2>
            <p>
              Our services are not intended for users under 18 years of age. We do not knowingly collect personal information 
              from children. If we become aware of such collection, we will delete the information immediately.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-3">9. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your own. We ensure appropriate 
              safeguards are in place to protect your data in accordance with privacy laws.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-3">10. Changes to Privacy Policy</h2>
            <p>
              We may update this Privacy Policy periodically. We will notify users of significant changes via email or website notice. 
              Continued use of our services after changes indicates acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-3">11. Contact Us</h2>
            <p>
              For privacy-related questions or to exercise your rights, contact us at:
            </p>
            <ul className="list-none space-y-1 ml-4 mt-2">
              <li>Email: privacy@dexreaction.com</li>
              <li>Support: Through our ticket system</li>
            </ul>
          </section>

          <div className="mt-8 pt-6 border-t border-purple-500/20">
            <p className="text-sm text-gray-400">
              By using Dexreaction services, you acknowledge that you have read and understood this Privacy Policy and consent 
              to the collection, use, and disclosure of your information as described.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
